import { useEffect, useMemo, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api, formatSAR, formatDate } from "@/lib/api";
import * as XLSX from "xlsx";
import {
  X, Users, DollarSign, Activity, Trophy, ChevronRight, Filter, Download, Edit2, Trash2, Wallet, History, FileSpreadsheet, FileText,
} from "lucide-react";
import DateFilterChips from "@/components/DateFilterChips";
import EditTransactionModal from "@/components/EditTransactionModal";
import PayoutModal from "@/components/PayoutModal";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

function MedalBadge({ rank }) {
  const colors = ["bg-gold text-charcoal-900", "bg-foreground/30 text-charcoal-900", "bg-amber-700 text-white"];
  if (rank > 2) return <span className="text-foreground/40 font-mono text-xs">#{rank + 1}</span>;
  return (
    <span className={`inline-flex h-7 w-7 rounded-full font-heading text-xs items-center justify-center ${colors[rank]}`}>
      {rank + 1}
    </span>
  );
}

export default function TherapistsReportPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "owner";
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // therapist_id
  const [detail, setDetail] = useState(null);
  const [payouts, setPayouts] = useState(null);     // { payouts:[], total_paid } | null=loading
  const [editingTxId, setEditingTxId] = useState(null);
  const [payoutFor, setPayoutFor] = useState(null);  // therapist object
  const [payoutCashier, setPayoutCashier] = useState(null);  // cashier object
  const [cashiers, setCashiers] = useState([]);
  const [cashiersLoading, setCashiersLoading] = useState(true);

  async function load() {
    setLoading(true);
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    try {
      const { data } = await api.get("/reports/therapists", { params });
      setList(data);
    } finally { setLoading(false); }
  }

  async function loadCashiers() {
    setCashiersLoading(true);
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    try {
      const { data } = await api.get("/reports/cashiers", { params });
      setCashiers(data);
    } finally { setCashiersLoading(false); }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadCashiers(); }, []);

  async function openDetail(th) {
    setSelected(th.id);
    setDetail(null);
    setPayouts(null);
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    // Fetch earnings detail + payout history in parallel
    const [detailRes, payoutsRes] = await Promise.all([
      api.get(`/reports/therapist/${th.id}`, { params }),
      api.get(`/therapists/${th.id}/payouts`),
    ]);
    setDetail(detailRes.data);
    setPayouts(payoutsRes.data);
  }

  async function reloadDetail() {
    if (!selected) return;
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    const [detailRes, payoutsRes] = await Promise.all([
      api.get(`/reports/therapist/${selected}`, { params }),
      api.get(`/therapists/${selected}/payouts`),
    ]);
    setDetail(detailRes.data);
    setPayouts(payoutsRes.data);
    load();
  }

  async function deletePayout(payoutId) {
    if (!window.confirm(
      "Hapus catatan pembayaran ini?\n\n" +
      "PERHATIAN: Menghapus payout akan memulihkan komisi terapis " +
      "kembali seperti belum dibayar. Aksi ini tidak bisa dibatalkan."
    )) return;
    try {
      await api.delete(`/therapists/payouts/${payoutId}`);
      toast.success("Riwayat payout dihapus & komisi dipulihkan");
      reloadDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus payout");
    }
  }

  // ── Export therapist detail (Excel / PDF) ──────────────────────────
  function _stamp() {
    return new Date().toLocaleString("id-ID", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: "Asia/Riyadh",
    });
  }
  function _rangeLabel() {
    if (!from && !to) return "Semua Periode";
    return `${from || "Awal"} — ${to || "Sekarang"}`;
  }
  function _escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ));
  }

  function exportDetailExcel() {
    if (!detail || !detail.rows) return;
    if (detail.rows.length === 0) { toast.error("Tidak ada data untuk diekspor"); return; }
    const name = detail.therapist?.name || "Terapis";
    const meta = [
      ["JAVA SPA — DETAIL KOMISI TERAPIS"],
      [],
      ["Terapis:", name],
      ["Telepon:", detail.therapist?.phone || "—"],
      ["Periode:", _rangeLabel()],
      ["Dicetak:", _stamp()],
      [],
      ["RINGKASAN"],
      ["Jumlah Sesi", detail.session_count],
      ["Jumlah Harga (SAR)", detail.total_price || 0],
      ["5% Pertama (SAR)", detail.total_base],
      ["Bonus 5% (SAR)", detail.total_bonus_5 || 0],
      ["Bonus 10% (SAR)", detail.total_bonus_10 || 0],
      ["Total Pendapatan (SAR)", detail.all_total],
      ["Total Sudah Dibayar (SAR)", detail.total_paid],
      ["Sisa/Pending (SAR)", detail.pending_commission],
      ["Terakhir Dibayar", detail.last_payout_at ? formatDate(detail.last_payout_at) : "—"],
      [],
      ["DETAIL SESI"],
      ["Tanggal", "Layanan", "Harga (SAR)", "Pelanggan", "5% Pertama", "Bonus 5%", "Bonus 10%", "Total Komisi"],
    ];
    const body = detail.rows.map((r) => [
      formatDate(r.date),
      r.service + ((r.bonus_5 || r.bonus_10) ? " (BONUS)" : ""),
      r.price ?? 0,
      r.customer_phone,
      r.commission_base,
      r.commission_bonus_5 || 0,
      r.commission_bonus_10 || 0,
      r.total,
    ]);
    const ws = XLSX.utils.aoa_to_sheet([...meta, ...body]);
    ws["!cols"] = [{ wch: 22 }, { wch: 28 }, { wch: 12 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }];
    // Also add a payout sheet if payouts exist
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Detail Sesi");
    if (payouts && payouts.payouts && payouts.payouts.length > 0) {
      const ph = [
        ["Tanggal Bayar", "Gaji (SAR)", "Komisi (SAR)", "Bonus Google (SAR)", "Total (SAR)", "Dibayar oleh", "Catatan"],
        ...payouts.payouts.map((p) => [
          formatDate(p.paid_at), p.gaji || 0, p.komisi || 0, p.bonus_google_amount || 0,
          p.amount || 0, p.paid_by_name || "—", p.notes || "",
        ]),
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(ph);
      ws2["!cols"] = [{ wch: 22 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 12 }, { wch: 20 }, { wch: 30 }];
      XLSX.utils.book_append_sheet(wb, ws2, "Riwayat Payout");
    }
    const safeName = name.replace(/[^a-z0-9]/gi, "-").toLowerCase();
    XLSX.writeFile(wb, `Java-SPA_Terapis_${safeName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Excel berhasil diunduh");
  }

  function exportDetailPDF() {
    if (!detail || !detail.rows) return;
    if (detail.rows.length === 0) { toast.error("Tidak ada data untuk diekspor"); return; }
    const name = detail.therapist?.name || "Terapis";
    const stamp = _stamp();
    const html = `<!doctype html><html lang="id"><head><meta charset="utf-8"><title>Java SPA — ${_escapeHtml(name)} — ${_rangeLabel()}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, sans-serif; color: #1c1c1c; padding: 24px; }
  .brand { border-bottom: 2px solid #b78638; padding-bottom: 12px; margin-bottom: 18px; }
  .brand h1 { margin: 0; font-size: 22px; color: #b78638; letter-spacing: 2px; }
  .brand .sub { color: #6b6b6b; font-size: 11px; margin-top: 4px; }
  .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 12px; font-size: 12px; margin-bottom: 18px; }
  .meta .k { color: #6b6b6b; }
  .stats { display: grid; grid-template-columns: repeat(6, 1fr); gap: 8px; margin-bottom: 16px; }
  .stat { border: 1px solid #ddd; border-radius: 6px; padding: 8px; text-align: center; }
  .stat .k { font-size: 9px; color: #6b6b6b; text-transform: uppercase; letter-spacing: 1px; }
  .stat .v { font-size: 14px; font-weight: 700; margin-top: 2px; }
  .stat.gold { border-color: #b78638; background: #fdf6e6; }
  .stat.gold .v { color: #b78638; }
  h3 { margin: 18px 0 8px; font-size: 13px; letter-spacing: 2px; text-transform: uppercase; color: #b78638; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th { background: #1c1c1c; color: #fff; padding: 6px 4px; text-align: start; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
  td { padding: 5px 4px; border-bottom: 1px solid #eee; }
  td.num, th.num { text-align: end; font-variant-numeric: tabular-nums; }
  .total-row td { background: #f5f5f5; font-weight: 700; padding-top: 8px; padding-bottom: 8px; }
  .footer { margin-top: 24px; font-size: 10px; color: #6b6b6b; text-align: center; }
  @media print { body { padding: 12px; } }
</style></head><body>
<div class="brand">
  <h1>JAVA SPA · DETAIL KOMISI TERAPIS</h1>
  <div class="sub">Riyadh · Luxury Spa · Trilingual POS</div>
</div>
<div class="meta">
  <div><span class="k">Terapis:</span> <b>${_escapeHtml(name)}</b></div>
  <div><span class="k">Periode:</span> <b>${_escapeHtml(_rangeLabel())}</b></div>
  <div><span class="k">Telepon:</span> ${_escapeHtml(detail.therapist?.phone || "-")}</div>
  <div><span class="k">Dicetak:</span> ${stamp}</div>
</div>
<div class="stats">
  <div class="stat"><div class="k">Sesi</div><div class="v">${detail.session_count}</div></div>
  <div class="stat"><div class="k">Jumlah Harga</div><div class="v">${formatSAR(detail.total_price || 0)}</div></div>
  <div class="stat"><div class="k">5% Pertama</div><div class="v">${formatSAR(detail.total_base)}</div></div>
  <div class="stat"><div class="k">Bonus 5%</div><div class="v">${formatSAR(detail.total_bonus_5 || 0)}</div></div>
  <div class="stat"><div class="k">Bonus 10%</div><div class="v">${formatSAR(detail.total_bonus_10 || 0)}</div></div>
  <div class="stat gold"><div class="k">Total Pendapatan</div><div class="v">${formatSAR(detail.all_total)}</div></div>
</div>
<h3>Detail Sesi</h3>
<table>
  <thead><tr>
    <th>Tanggal</th><th>Layanan</th><th class="num">Harga</th><th>Pelanggan</th>
    <th class="num">5% Pertama</th><th class="num">Bonus 5%</th><th class="num">Bonus 10%</th><th class="num">Total</th>
  </tr></thead>
  <tbody>
    ${detail.rows.map((r) => `<tr>
      <td>${_escapeHtml(formatDate(r.date))}</td>
      <td>${_escapeHtml(r.service || "")}${(r.bonus_5 || r.bonus_10) ? " <span style='color:#4a7c59;font-weight:600;font-size:9px'>BONUS</span>" : ""}</td>
      <td class="num">${formatSAR(r.price || 0)}</td>
      <td>${_escapeHtml(r.customer_phone || "")}</td>
      <td class="num">${formatSAR(r.commission_base)}</td>
      <td class="num">${(r.commission_bonus_5 || 0) > 0 ? formatSAR(r.commission_bonus_5) : "—"}</td>
      <td class="num">${(r.commission_bonus_10 || 0) > 0 ? formatSAR(r.commission_bonus_10) : "—"}</td>
      <td class="num">${formatSAR(r.total)}</td>
    </tr>`).join("")}
    <tr class="total-row">
      <td colspan="4" style="text-align:end">TOTAL</td>
      <td class="num">${formatSAR(detail.total_base)}</td>
      <td class="num">${formatSAR(detail.total_bonus_5 || 0)}</td>
      <td class="num">${formatSAR(detail.total_bonus_10 || 0)}</td>
      <td class="num">${formatSAR(detail.all_total)}</td>
    </tr>
  </tbody>
</table>
${payouts && payouts.payouts && payouts.payouts.length > 0 ? `
<h3>Riwayat Pembayaran Komisi</h3>
<table>
  <thead><tr>
    <th>Tanggal Bayar</th><th class="num">Gaji</th><th class="num">Komisi</th>
    <th class="num">Bonus Google</th><th class="num">Total</th><th>Dibayar oleh</th><th>Catatan</th>
  </tr></thead>
  <tbody>${payouts.payouts.map((p) => `<tr>
    <td>${_escapeHtml(formatDate(p.paid_at))}</td>
    <td class="num">${(p.gaji || 0) > 0 ? formatSAR(p.gaji) : "—"}</td>
    <td class="num">${(p.komisi || 0) > 0 ? formatSAR(p.komisi) : "—"}</td>
    <td class="num">${(p.bonus_google_amount || 0) > 0 ? formatSAR(p.bonus_google_amount) : "—"}</td>
    <td class="num">${formatSAR(p.amount || 0)}</td>
    <td>${_escapeHtml(p.paid_by_name || "—")}</td>
    <td>${_escapeHtml(p.notes || "")}</td>
  </tr>`).join("")}</tbody>
</table>` : ""}
<div class="footer">Java SPA · Riyadh · Dokumen ini dicetak otomatis dari sistem POS pada ${stamp}.</div>
<script>window.onload = () => setTimeout(() => window.print(), 300);</script>
</body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) toast.error("Popup diblokir — mohon izinkan popup untuk ekspor PDF");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }


  async function loadTxForEdit(txId) {
    try {
      const { data } = await api.get("/transactions", { params: { include_cancelled: true, page_size: 500 } });
      const tx = (data.items || []).find((t) => t.id === txId);
      if (tx) setEditingTxId(tx);
      else toast.error("Transaksi tidak ditemukan");
    } catch (e) {
      toast.error("Error");
    }
  }

  async function deleteRow(txId) {
    if (!window.confirm(t("tx.deleteConfirm"))) return;
    try {
      await api.delete(`/transactions/${txId}`);
      toast.success(t("tx.deleteSuccess"));
      reloadDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }

  const totals = useMemo(() => {
    const price = list.reduce((s, t) => s + (t.total_price || 0), 0);
    const payout = list.reduce((s, t) => s + t.all_total, 0);
    const sessions = list.reduce((s, t) => s + t.session_count, 0);
    const activeCount = list.filter((t) => t.session_count > 0).length;
    const pending = list.reduce((s, t) => s + (t.pending_commission || 0), 0);
    const paid = list.reduce((s, t) => s + (t.total_paid || 0), 0);
    return { price, payout, sessions, activeCount, pending, paid };
  }, [list]);

  function exportCsv() {
    const headers = ["Name", "Phone", "Sessions", "Total Price", "Base 5%", "Bonus 5%", "This Month", "Total Earned", "Total Paid", "Pending", "Last Session"];
    const lines = [headers.join(",")];
    list.forEach((r) => {
      lines.push([
        `"${r.name}"`, r.phone, r.session_count,
        r.total_price || 0,
        r.total_base, r.total_upsell, r.month_total, r.all_total,
        r.total_paid || 0, r.pending_commission || 0,
        r.last_session ? formatDate(r.last_session) : "—",
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `therapists-recap-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-gold">— HR · Payout</span>
          <h1 className="text-3xl md:text-4xl font-heading mt-2">{t("ther.reportTitle")}</h1>
          <p className="text-foreground/50 mt-1 max-w-xl">{t("ther.reportSubtitle")}</p>
        </div>
        <button onClick={exportCsv} data-testid="therapists-export-btn" className="btn-outline-gold">
          <Download className="h-4 w-4" /> CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card-luxury p-5 space-y-4">
        <DateFilterChips
          from={from}
          to={to}
          onChange={(f, tt) => {
            setFrom(f); setTo(tt);
            setTimeout(load, 0);
          }}
          onClear={() => { setFrom(""); setTo(""); setTimeout(load, 0); }}
        />
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">{t("reports.from")}</label>
            <input type="date" data-testid="thr-filter-from" value={from} onChange={(e) => setFrom(e.target.value)}
              className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">{t("reports.to")}</label>
            <input type="date" data-testid="thr-filter-to" value={to} onChange={(e) => setTo(e.target.value)}
              className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2" />
          </div>
          <button onClick={load} data-testid="thr-apply-btn" className="btn-gold mt-5 justify-center md:col-span-1">
            <Filter className="h-4 w-4" /> Apply
          </button>
          <button onClick={() => { setFrom(""); setTo(""); setTimeout(load, 0); }} className="btn-outline-gold mt-5 justify-center md:col-span-1">
            Reset
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card-luxury accent-gold p-5" data-testid="summary-price">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gold/15 text-gold grid place-items-center"><DollarSign className="h-5 w-5" /></div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("ther.totalPrice")}</div>
              <div className="text-2xl md:text-3xl font-heading mt-1 text-gold">{formatSAR(totals.price)}</div>
            </div>
          </div>
        </div>
        <div className="card-luxury accent-gold p-5" data-testid="summary-payout">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gold/15 text-gold grid place-items-center"><DollarSign className="h-5 w-5" /></div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("ther.totalPayout")}</div>
              <div className="text-2xl md:text-3xl font-heading mt-1 text-gold">{formatSAR(totals.payout)}</div>
            </div>
          </div>
        </div>
        <div className="card-luxury accent-leaf p-5" data-testid="summary-sessions">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-leaf/15 text-leaf grid place-items-center"><Activity className="h-5 w-5" /></div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("ther.totalSessions")}</div>
              <div className="text-2xl md:text-3xl font-heading mt-1">{totals.sessions}</div>
            </div>
          </div>
        </div>
        <div className="card-luxury accent-gold p-5" data-testid="summary-pending">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gold/15 text-gold grid place-items-center"><Wallet className="h-5 w-5" /></div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("ther.pending")}</div>
              <div className="text-2xl md:text-3xl font-heading mt-1 text-gold">{formatSAR(totals.pending)}</div>
              <div className="text-[10px] text-leaf/80 mt-0.5">{t("ther.paid")}: {formatSAR(totals.paid)}</div>
            </div>
          </div>
        </div>
        <div className="card-luxury accent-leaf p-5" data-testid="summary-active">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-leaf/15 text-leaf grid place-items-center"><Users className="h-5 w-5" /></div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("ther.activeTherapists")}</div>
              <div className="text-2xl md:text-3xl font-heading mt-1">{totals.activeCount} / {list.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Therapist Ranking Cards */}
      <div className="space-y-3" data-testid="therapist-section">
        <h2 className="text-xl md:text-2xl font-heading text-gold">— Rekap Terapis</h2>
        {loading && <div className="text-center py-12 text-foreground/40">{t("common.loading")}</div>}
        {!loading && list.length === 0 && <div className="text-center py-12 text-foreground/40">{t("common.empty")}</div>}
        {!loading && list.map((th, idx) => (
          <div
            key={th.id}
            data-testid={`therapist-row-${th.id}`}
            className="card-luxury p-5 hover:border-gold/40 transition-colors"
          >
            <div className="grid md:grid-cols-[repeat(16,minmax(0,1fr))] gap-3 items-center">
              {/* Rank + name */}
              <div className="md:col-span-3 flex items-center gap-3">
                <MedalBadge rank={idx} />
                <div>
                  <div className="font-heading text-lg flex items-center gap-2">
                    {th.name}
                    {idx === 0 && th.all_total > 0 && <Trophy className="h-4 w-4 text-gold" />}
                  </div>
                  <div className="text-xs text-foreground/50">{th.phone}</div>
                </div>
              </div>

              {/* Stats */}
              <div className="md:col-span-1 text-center md:text-start">
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">{t("ther.sessions")}</div>
                <div className="text-xl font-heading mt-1">{th.session_count}</div>
              </div>
              <div className="md:col-span-2 text-center md:text-start" data-testid={`col-price-${th.id}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">{t("ther.totalPrice")}</div>
                <div className="text-lg font-heading mt-1 text-gold">{formatSAR(th.total_price || 0)}</div>
              </div>
              <div className="md:col-span-2 text-center md:text-start" data-testid={`col-base-${th.id}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">{t("ther.base")}</div>
                <div className="text-lg font-heading mt-1">{formatSAR(th.total_base)}</div>
              </div>
              <div className="md:col-span-2 text-center md:text-start" data-testid={`col-bonus5-${th.id}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">{t("ther.bonus5")}</div>
                <div className="text-lg font-heading mt-1 text-leaf/80">
                  {(th.total_bonus_5 || 0) > 0 ? formatSAR(th.total_bonus_5) : <span className="text-foreground/30">—</span>}
                </div>
              </div>
              <div className="md:col-span-2 text-center md:text-start" data-testid={`col-bonus10-${th.id}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">{t("ther.bonus10")}</div>
                <div className="text-lg font-heading mt-1 text-leaf">
                  {(th.total_bonus_10 || 0) > 0 ? formatSAR(th.total_bonus_10) : <span className="text-foreground/30">—</span>}
                </div>
              </div>
              <div className="md:col-span-2 text-center md:text-start">
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">{t("ther.pending")}</div>
                <div
                  className={`text-2xl font-heading mt-1 ${(th.pending_commission || 0) > 0 ? "text-gold" : "text-foreground/40"}`}
                  data-testid={`therapist-pending-${th.id}`}
                >
                  {formatSAR(th.pending_commission || 0)}
                </div>
                {(th.total_paid || 0) > 0 && (
                  <div className="text-[10px] text-leaf/80 mt-0.5">
                    {t("ther.paid")}: {formatSAR(th.total_paid)}
                  </div>
                )}
              </div>
              <div className="md:col-span-2 flex md:flex-col gap-2 md:items-end">
                {canManage && (
                  <button
                    onClick={() => setPayoutFor(th)}
                    data-testid={`therapist-paycommission-${th.id}`}
                    className="inline-flex items-center gap-1.5 text-xs btn-leaf py-2 px-3"
                  >
                    <Wallet className="h-3.5 w-3.5" /> {t("ther.payCommission")}
                  </button>
                )}
                <button
                  onClick={() => openDetail(th)}
                  data-testid={`therapist-detail-${th.id}`}
                  className="inline-flex items-center gap-1 text-sm text-gold hover:text-gold-light"
                >
                  {t("ther.viewDetails")} <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Bottom strip */}
            <div className="mt-4 pt-3 border-t border-gold/10 flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-foreground/50 uppercase tracking-[0.2em] text-[10px]">{t("ther.topServices")}:</span>
                {th.top_services.length === 0 && <span className="text-foreground/40">—</span>}
                {th.top_services.map((s) => (
                  <span key={s.name} className="bg-charcoal-900 border border-gold/15 rounded-full px-2 py-0.5">
                    {s.name} <span className="text-foreground/40">×{s.count}</span>
                  </span>
                ))}
              </div>
              <div className="text-foreground/50">
                {t("ther.totalEarned")}: <span className="text-gold font-medium">{formatSAR(th.all_total)}</span>
                {" · "}
                {t("ther.thisMonth")}: <span className="text-leaf font-medium">{formatSAR(th.month_total)}</span>
                {th.last_session && <> · {t("ther.lastSession")}: {formatDate(th.last_session)}</>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-4" onClick={() => setSelected(null)}>
          <div className="card-luxury p-6 w-full max-w-5xl max-h-[85vh] overflow-y-auto scrollbar-thin" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-5 gap-3 flex-wrap">
              <div>
                <h2 className="text-2xl font-heading">{detail?.therapist?.name || "…"}</h2>
                <div className="text-sm text-foreground/50">{detail?.therapist?.phone}</div>
              </div>
              <div className="flex items-center gap-2 ms-auto">
                {detail && (
                  <>
                    <button
                      onClick={exportDetailExcel}
                      data-testid="therapist-detail-excel"
                      className="btn-leaf text-xs py-2 px-3"
                      title="Ekspor Excel (.xlsx)"
                    >
                      <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
                    </button>
                    <button
                      onClick={exportDetailPDF}
                      data-testid="therapist-detail-pdf"
                      className="btn-outline-gold text-xs py-2 px-3"
                      title="Ekspor / Cetak PDF"
                    >
                      <FileText className="h-3.5 w-3.5" /> PDF
                    </button>
                  </>
                )}
                <button
                  onClick={() => setSelected(null)}
                  data-testid="detail-close-btn"
                  className="p-2 rounded-md hover:bg-charcoal-900 transition"
                  aria-label="Tutup"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {!detail && <div className="text-center py-10 text-foreground/40">{t("common.loading")}</div>}
            {detail && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
                  <div className="bg-charcoal-900/60 rounded-lg p-3">
                    <div className="text-[10px] uppercase text-foreground/50">{t("ther.sessions")}</div>
                    <div className="text-xl font-heading">{detail.session_count}</div>
                  </div>
                  <div className="bg-charcoal-900/60 rounded-lg p-3">
                    <div className="text-[10px] uppercase text-foreground/50">{t("ther.base")}</div>
                    <div className="text-xl font-heading">{formatSAR(detail.total_base)}</div>
                  </div>
                  <div className="bg-charcoal-900/60 rounded-lg p-3">
                    <div className="text-[10px] uppercase text-foreground/50">{t("ther.bonus5")}</div>
                    <div className="text-xl font-heading text-leaf/80">{formatSAR(detail.total_bonus_5 || 0)}</div>
                  </div>
                  <div className="bg-charcoal-900/60 rounded-lg p-3">
                    <div className="text-[10px] uppercase text-foreground/50">{t("ther.bonus10")}</div>
                    <div className="text-xl font-heading text-leaf">{formatSAR(detail.total_bonus_10 || 0)}</div>
                  </div>
                  <div className="bg-gold/10 border border-gold/30 rounded-lg p-3">
                    <div className="text-[10px] uppercase text-foreground/50">{t("ther.totalEarned")}</div>
                    <div className="text-xl font-heading text-gold">{formatSAR(detail.all_total)}</div>
                  </div>
                </div>

                <div className="overflow-x-auto scrollbar-thin">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.15em] text-foreground/50 bg-charcoal-900/60">
                      <tr>
                        <th className="text-start px-3 py-2">{t("reports.date")}</th>
                        <th className="text-start px-3 py-2">{t("reports.services")}</th>
                        {canManage && <th className="text-end px-3 py-2">Harga</th>}
                        <th className="text-start px-3 py-2">{t("reports.customer")}</th>
                        <th className="text-end px-3 py-2">{t("ther.base")}</th>
                        <th className="text-end px-3 py-2">{t("ther.bonus5")}</th>
                        <th className="text-end px-3 py-2">{t("ther.bonus10")}</th>
                        <th className="text-end px-3 py-2">{t("reports.total")}</th>
                        {canManage && <th className="text-end px-3 py-2">{t("common.actions")}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {detail.rows.length === 0 && <tr><td colSpan={canManage ? 9 : 7} className="text-center py-6 text-foreground/40">{t("common.empty")}</td></tr>}
                      {detail.rows.map((r, i) => (
                        <tr key={r.tx_id ? `${r.tx_id}-${i}` : i} className="border-t border-charcoal-600/40">
                          <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.date)}</td>
                          <td className="px-3 py-2">
                            {r.service}
                            {(r.bonus_5 || r.bonus_10) && <span className="text-leaf text-[10px] ms-1">BONUS</span>}
                          </td>
                          {canManage && <td className="px-3 py-2 text-end text-gold font-mono whitespace-nowrap">{r.price != null ? formatSAR(r.price) : "—"}</td>}
                          <td className="px-3 py-2 text-foreground/60">{r.customer_phone}</td>
                          <td className="px-3 py-2 text-end">{formatSAR(r.commission_base)}</td>
                          <td className="px-3 py-2 text-end text-leaf/80">
                            {(r.commission_bonus_5 || 0) > 0 ? formatSAR(r.commission_bonus_5) : <span className="text-foreground/30">—</span>}
                          </td>
                          <td className="px-3 py-2 text-end text-leaf">
                            {(r.commission_bonus_10 || 0) > 0 ? formatSAR(r.commission_bonus_10) : <span className="text-foreground/30">—</span>}
                          </td>
                          <td className="px-3 py-2 text-end text-gold font-medium">{formatSAR(r.total)}</td>
                          {canManage && (
                            <td className="px-3 py-2 text-end whitespace-nowrap">
                              <button
                                onClick={() => loadTxForEdit(r.tx_id)}
                                data-testid={`th-detail-edit-${r.tx_id}`}
                                className="p-1.5 rounded-full hover:bg-gold/10 text-gold inline-flex me-1"
                                title={t("tx.edit")}
                              >
                                <Edit2 className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => deleteRow(r.tx_id)}
                                data-testid={`th-detail-del-${r.tx_id}`}
                                className="p-1.5 rounded-full hover:bg-red-500/10 text-red-400 inline-flex"
                                title={t("tx.delete")}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ── Payout history ─────────────────────────────────── */}
                <div className="mt-6" data-testid="therapist-detail-payouts">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <History className="h-4 w-4 text-gold" />
                      <h3 className="font-heading text-sm uppercase tracking-[0.2em] text-foreground/70">
                        Riwayat Pembayaran
                      </h3>
                    </div>
                    {payouts && (
                      <div className="text-xs text-foreground/50">
                        Total dibayarkan seumur hidup:{" "}
                        <span className="font-heading text-gold">{formatSAR(payouts.total_paid || 0)}</span>
                      </div>
                    )}
                  </div>

                  {!payouts && (
                    <div className="text-center py-4 text-foreground/40 text-xs">
                      {t("common.loading")}
                    </div>
                  )}
                  {payouts && payouts.payouts.length === 0 && (
                    <div className="text-center py-6 text-foreground/40 text-xs border border-dashed border-foreground/10 rounded-lg">
                      Belum ada pembayaran komisi tercatat.
                    </div>
                  )}
                  {payouts && payouts.payouts.length > 0 && (
                    <div className="overflow-x-auto scrollbar-thin">
                      <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-foreground/50 bg-charcoal-900/60">
                          <tr>
                            <th className="text-start px-3 py-2">Tanggal</th>
                            <th className="text-end px-3 py-2">Gaji</th>
                            <th className="text-end px-3 py-2">Komisi</th>
                            <th className="text-end px-3 py-2">Bonus Google</th>
                            <th className="text-end px-3 py-2">Total</th>
                            <th className="text-start px-3 py-2">Dibayar oleh</th>
                            <th className="text-start px-3 py-2">Catatan</th>
                            {canManage && <th className="text-end px-3 py-2">Aksi</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {payouts.payouts.map((p) => (
                            <tr
                              key={p.id}
                              className="border-b border-foreground/5 last:border-0 hover:bg-charcoal-900/40 transition-colors"
                              data-testid={`payout-row-${p.id}`}
                            >
                              <td className="px-3 py-2 whitespace-nowrap text-foreground/80">
                                {formatDate(p.paid_at)}
                              </td>
                              <td className="px-3 py-2 text-end">
                                {p.gaji > 0 ? formatSAR(p.gaji) : <span className="text-foreground/30">—</span>}
                              </td>
                              <td className="px-3 py-2 text-end text-leaf">
                                {p.komisi > 0 ? formatSAR(p.komisi) : <span className="text-foreground/30">—</span>}
                              </td>
                              <td className="px-3 py-2 text-end">
                                {p.bonus_google_amount > 0
                                  ? formatSAR(p.bonus_google_amount)
                                  : <span className="text-foreground/30">—</span>}
                              </td>
                              <td className="px-3 py-2 text-end font-heading text-gold">
                                {formatSAR(p.amount)}
                              </td>
                              <td className="px-3 py-2 text-foreground/60 text-xs">
                                {p.paid_by_name || "—"}
                              </td>
                              <td className="px-3 py-2 text-foreground/60 text-xs italic">
                                {p.notes || <span className="text-foreground/30 not-italic">—</span>}
                                {p.bonus_google_names && (
                                  <div className="text-[10px] text-foreground/40 mt-0.5 not-italic">
                                    Bonus: {p.bonus_google_names}
                                  </div>
                                )}
                              </td>
                              {canManage && (
                                <td className="px-3 py-2 text-end">
                                  <button
                                    onClick={() => deletePayout(p.id)}
                                    data-testid={`payout-delete-${p.id}`}
                                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-red-500/30 text-red-300 hover:bg-red-500/10 hover:border-red-500 transition active:scale-95"
                                    title="Hapus & pulihkan komisi"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* CASHIERS SECTION */}
      <div className="space-y-3" data-testid="cashier-section">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl md:text-2xl font-heading text-leaf">— Rekap Kasir</h2>
          </div>
        </div>

        {cashiersLoading && (
          <div className="card-luxury p-8 text-center text-foreground/40">{t("common.loading")}</div>
        )}
        {!cashiersLoading && cashiers.length === 0 && (
          <div className="card-luxury p-8 text-center text-foreground/40 border-dashed">
            Belum ada kasir terdaftar. Tambahkan via Menu Pengguna.
          </div>
        )}
        {!cashiersLoading && cashiers.map((c) => (
          <div
            key={c.id}
            data-testid={`cashier-row-${c.id}`}
            className="card-luxury p-5 hover:border-leaf/40 transition-colors"
          >
            <div className="grid md:grid-cols-12 gap-4 items-center">
              <div className="md:col-span-3 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-leaf/15 text-leaf grid place-items-center font-heading">
                  {c.name?.[0]?.toUpperCase() || "K"}
                </div>
                <div>
                  <div className="font-heading text-lg">{c.name}</div>
                  <div className="text-xs text-foreground/50">{c.phone}</div>
                </div>
              </div>
              <div className="md:col-span-2 text-center md:text-start">
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">Transaksi</div>
                <div className="text-xl font-heading mt-1">{c.tx_count}</div>
              </div>
              <div className="md:col-span-3 text-center md:text-start">
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">Total Revenue Ditangani</div>
                <div className="text-lg font-heading mt-1 text-leaf">{formatSAR(c.revenue_handled)}</div>
              </div>
              <div className="md:col-span-2 text-center md:text-start">
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">{t("ther.paid")}</div>
                <div className="text-lg font-heading mt-1 text-gold">{formatSAR(c.total_paid)}</div>
              </div>
              <div className="md:col-span-2 flex md:justify-end">
                {canManage && (
                  <button
                    onClick={() => setPayoutCashier(c)}
                    data-testid={`cashier-pay-${c.id}`}
                    className="inline-flex items-center gap-1.5 text-xs btn-leaf py-2 px-3"
                  >
                    <Wallet className="h-3.5 w-3.5" /> Bayar Gaji
                  </button>
                )}
              </div>
            </div>
            {c.last_tx && (
              <div className="mt-3 pt-3 border-t border-leaf/10 text-xs text-foreground/50">
                Transaksi terakhir: {formatDate(c.last_tx)}
              </div>
            )}
          </div>
        ))}
      </div>

      {editingTxId && (
        <EditTransactionModal
          tx={editingTxId}
          onClose={() => setEditingTxId(null)}
          onSaved={() => { setEditingTxId(null); reloadDetail(); }}
        />
      )}

      {payoutFor && (
        <PayoutModal
          therapist={payoutFor}
          mode="terapis"
          onClose={() => setPayoutFor(null)}
          onSaved={() => { load(); }}
        />
      )}

      {payoutCashier && (
        <PayoutModal
          therapist={payoutCashier}
          mode="kasir"
          onClose={() => setPayoutCashier(null)}
          onSaved={() => { loadCashiers(); }}
        />
      )}
    </div>
  );
}
